import { supabase } from '../lib/supabase';

export const cancellationService = {
  /**
   * Get all cancellation requests for organizer's events
   * @param {string} organizerId - The organizer's user ID
   * @returns {Promise<Array>} Array of cancellation requests with event and participant details
   */
  async getOrganizerCancellationRequests(organizerId) {
    // CRITICAL FIX: Added !inner to ensure proper join filtering by organizer_id
    const { data, error } = await supabase
      ?.from('event_registrations')
      ?.select(`
        *,
        event:events!inner(
          id,
          title,
          event_date,
          event_time,
          location,
          organizer_id
        ),
        participant:user_profiles!user_id(
          id,
          full_name,
          email,
          phone
        )
      `)
      ?.eq('event.organizer_id', organizerId)
      ?.not('cancellation_requested_at', 'is', null)
      ?.is('cancellation_processed_at', null)
      ?.order('cancellation_requested_at', { ascending: false });

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      eventId: row?.event_id,
      userId: row?.user_id,
      status: row?.status,
      cancellationRequestedAt: row?.cancellation_requested_at,
      cancellationRequestReason: row?.cancellation_request_reason,
      participantName: row?.participant_name,
      participantEmail: row?.participant_email,
      participantPhone: row?.participant_phone,
      equipmentRental: row?.equipment_rental,
      specialRequirements: row?.special_requirements,
      createdAt: row?.created_at,
      event: row?.event ? {
        id: row?.event?.id,
        title: row?.event?.title,
        eventDate: row?.event?.event_date,
        eventTime: row?.event?.event_time,
        location: row?.event?.location
      } : null,
      participant: row?.participant ? {
        id: row?.participant?.id,
        fullName: row?.participant?.full_name,
        email: row?.participant?.email,
        phone: row?.participant?.phone
      } : null
    })) || [];
  },

  /**
   * Get all cancellation requests for organizer's events with advanced filtering
   * @param {string} organizerId - The organizer's user ID
   * @param {Object} filters - Filter options
   * @returns {Promise<Array>} Array of cancellation requests
   */
  async getOrganizerCancellationRequestsWithFilters(organizerId, filters = {}) {
    // CRITICAL FIX: Added !inner to ensure proper join filtering
    let query = supabase
      ?.from('event_registrations')
      ?.select(`
        *,
        event:events!inner(
          id,
          title,
          event_date,
          event_time,
          location,
          organizer_id,
          price_own_board,
          price_with_rental
        ),
        participant:user_profiles!user_id(
          id,
          full_name,
          email,
          phone
        )
      `)
      ?.eq('event.organizer_id', organizerId);

    // Status filter
    if (filters?.status && filters?.status !== 'all') {
      if (filters?.status === 'pending') {
        query = query?.not('cancellation_requested_at', 'is', null)?.is('cancellation_processed_at', null);
      } else if (filters?.status === 'approved') {
        query = query?.eq('status', 'cancelled')?.not('cancellation_processed_at', 'is', null);
      } else if (filters?.status === 'rejected') {
        query = query?.not('cancellation_requested_at', 'is', null)?.not('cancellation_processed_at', 'is', null)?.neq('status', 'cancelled');
      }
    } else {
      // Default: show all requests that have been requested for cancellation
      query = query?.not('cancellation_requested_at', 'is', null);
    }

    // Decision date range filter
    if (filters?.dateFrom) {
      query = query?.gte('cancellation_processed_at', filters?.dateFrom);
    }
    if (filters?.dateTo) {
      const endDate = new Date(filters.dateTo);
      endDate?.setHours(23, 59, 59, 999);
      query = query?.lte('cancellation_processed_at', endDate?.toISOString());
    }

    query = query?.order('cancellation_requested_at', { ascending: false });

    const { data, error } = await query;

    if (error) throw error;

    let results = data?.map(row => ({
      id: row?.id,
      eventId: row?.event_id,
      userId: row?.user_id,
      status: row?.status,
      cancellationRequestedAt: row?.cancellation_requested_at,
      cancellationProcessedAt: row?.cancellation_processed_at,
      cancellationRequestReason: row?.cancellation_request_reason,
      participantName: row?.participant_name,
      participantEmail: row?.participant_email,
      participantPhone: row?.participant_phone,
      equipmentRental: row?.equipment_rental,
      specialRequirements: row?.special_requirements,
      createdAt: row?.created_at,
      event: row?.event ? {
        id: row?.event?.id,
        title: row?.event?.title,
        eventDate: row?.event?.event_date,
        eventTime: row?.event?.event_time,
        location: row?.event?.location,
        priceOwnBoard: row?.event?.price_own_board,
        priceWithRental: row?.event?.price_with_rental
      } : null,
      participant: row?.participant ? {
        id: row?.participant?.id,
        fullName: row?.participant?.full_name,
        email: row?.participant?.email,
        phone: row?.participant?.phone
      } : null
    })) || [];

    // Client-side refund amount filtering (since it's calculated, not stored)
    if (filters?.refundMin || filters?.refundMax) {
      results = results?.filter(request => {
        const refundAmount = this.calculateRefundAmount(request);
        const minMatch = !filters?.refundMin || refundAmount >= parseFloat(filters?.refundMin);
        const maxMatch = !filters?.refundMax || refundAmount <= parseFloat(filters?.refundMax);
        return minMatch && maxMatch;
      });
    }

    return results;
  },

  /**
   * Calculate refund amount based on request and event timing
   * @param {Object} request - Cancellation request object
   * @returns {number} Refund amount
   */
  calculateRefundAmount(request) {
    if (!request?.event) return 0;
    const hoursUntilEvent = (new Date(request.event.eventDate)?.getTime() - new Date()?.getTime()) / (1000 * 60 * 60);
    const basePrice = request?.equipmentRental ? 
      (request?.event?.priceWithRental || 2000) : 
      (request?.event?.priceOwnBoard || 500);
    
    if (hoursUntilEvent >= 72) return basePrice;
    if (hoursUntilEvent >= 48) return Math.round(basePrice * 0.75);
    if (hoursUntilEvent >= 24) return Math.round(basePrice * 0.5);
    return 0;
  },

  /**
   * Bulk approve cancellation requests
   * @param {Array<string>} registrationIds - Array of registration IDs
   * @param {string} organizerResponse - Organizer's notes/reason
   * @returns {Promise<Object>} Results object with success and failure counts
   */
  async bulkApproveCancellations(registrationIds, organizerResponse) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    let results = {
      successful: 0,
      failed: 0,
      errors: []
    };

    for (const registrationId of registrationIds) {
      try {
        await this.approveCancellation(registrationId, organizerResponse);
        results.successful++;
      } catch (error) {
        results.failed++;
        results?.errors?.push({
          registrationId,
          error: error?.message || 'Unknown error'
        });
      }
    }

    return results;
  },

  /**
   * Bulk reject cancellation requests
   * @param {Array<string>} registrationIds - Array of registration IDs
   * @param {string} organizerResponse - Organizer's rejection reason
   * @returns {Promise<Object>} Results object with success and failure counts
   */
  async bulkRejectCancellations(registrationIds, organizerResponse) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    let results = {
      successful: 0,
      failed: 0,
      errors: []
    };

    for (const registrationId of registrationIds) {
      try {
        await this.rejectCancellation(registrationId, organizerResponse);
        results.successful++;
      } catch (error) {
        results.failed++;
        results?.errors?.push({
          registrationId,
          error: error?.message || 'Unknown error'
        });
      }
    }

    return results;
  },

  /**
   * Approve a cancellation request
   * @param {string} registrationId - Registration ID
   * @param {string} organizerResponse - Organizer's notes/reason
   * @returns {Promise<Object>} Updated registration
   */
  async approveCancellation(registrationId, organizerResponse) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('event_registrations')?.update({
        status: 'cancelled',
        organizer_cancellation_response: organizerResponse,
        cancellation_processed_by: user?.id,
        cancellation_processed_at: new Date()?.toISOString()
      })?.eq('id', registrationId)?.select(`
        *,
        event:events(title, event_date, event_time, location),
        participant:user_profiles!user_id(full_name, email)
      `)?.single();

    if (error) throw error;

    // Send email notification with Turkish messages
    try {
      const recipientEmail = data?.participant?.email || data?.participant_email;
      const participantName = data?.participant?.full_name || data?.participant_name;
      
      if (recipientEmail) {
        await supabase?.functions?.invoke('send-email', {
          body: {
            to: recipientEmail,
            subject: `İptal Talebiniz Onaylandı - ${data?.event?.title}`,
            participantName,
            eventTitle: data?.event?.title,
            decision: 'approved',organizerNotes: organizerResponse,type: 'cancellation_decision'
          }
        });
      }
    } catch (emailError) {
      console.error('Failed to send approval email:', emailError);
      // Don't throw error - cancellation was processed successfully
    }

    // Send in-app notification to the participant
    try {
      const participantUserId = data?.user_id;
      if (participantUserId) {
        await supabase?.from('notifications')?.insert({
          user_id: participantUserId,
          title: 'Rezervasyonunuz İptal Edildi',
          message: `"${data?.event?.title}" etkinliğindeki rezervasyonunuz yönetici tarafından iptal edildi.${organizerResponse ? ` Neden: ${organizerResponse}` : ''}`,
          type: 'warning',
          read: false,
          action_url: '/my-tickets'
        });
      }
    } catch (notifError) {
      console.error('Failed to send in-app notification:', notifError);
      // Don't throw error - cancellation was processed successfully
    }

    return {
      id: data?.id,
      status: data?.status,
      organizerResponse: data?.organizer_cancellation_response,
      processedAt: data?.cancellation_processed_at
    };
  },

  /**
   * Reject a cancellation request
   * @param {string} registrationId - Registration ID
   * @param {string} organizerResponse - Organizer's rejection reason
   * @returns {Promise<Object>} Updated registration
   */
  async rejectCancellation(registrationId, organizerResponse) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('event_registrations')?.update({
        organizer_cancellation_response: organizerResponse,
        cancellation_processed_by: user?.id,
        cancellation_processed_at: new Date()?.toISOString(),
        cancellation_requested_at: null,
        cancellation_request_reason: null
      })?.eq('id', registrationId)?.select(`
        *,
        event:events(title, event_date, event_time, location),
        participant:user_profiles!user_id(full_name, email)
      `)?.single();

    if (error) throw error;

    // Send email notification with Turkish messages
    try {
      const recipientEmail = data?.participant?.email || data?.participant_email;
      const participantName = data?.participant?.full_name || data?.participant_name;
      
      if (recipientEmail) {
        await supabase?.functions?.invoke('send-email', {
          body: {
            to: recipientEmail,
            subject: `İptal Talebiniz Reddedildi - ${data?.event?.title}`,
            participantName,
            eventTitle: data?.event?.title,
            decision: 'rejected',
            organizerNotes: organizerResponse,
            type: 'cancellation_decision'
          }
        });
      }
    } catch (emailError) {
      console.error('Failed to send rejection email:', emailError);
      // Don't throw error - cancellation was processed successfully
    }

    // Send in-app notification to the participant
    try {
      const participantUserId = data?.user_id;
      if (participantUserId) {
        await supabase?.from('notifications')?.insert({
          user_id: participantUserId,
          title: 'İptal Talebiniz Reddedildi',
          message: `"${data?.event?.title}" etkinliği için iptal talebiniz reddedildi. Rezervasyonunuz aktif olmaya devam etmektedir.${organizerResponse ? ` Neden: ${organizerResponse}` : ''}`,
          type: 'error',
          read: false,
          action_url: '/my-tickets'
        });
      }
    } catch (notificationError) {
      console.error('Failed to send rejection notification:', notificationError);
      // Don't throw error - rejection was processed successfully
    }

    return {
      id: data?.id,
      status: data?.status,
      organizerResponse: data?.organizer_cancellation_response,
      processedAt: data?.cancellation_processed_at
    };
  },

  /**
   * Send email notification about cancellation decision (DEPRECATED - Use inline email sending)
   * @deprecated This method is deprecated, email sending is now handled inline in approve/reject methods
   */
  async sendCancellationDecisionEmail(recipientEmail, participantName, eventTitle, decision, organizerNotes) {
    console.warn('sendCancellationDecisionEmail is deprecated. Email sending is now handled inline in approve/reject methods.');
  },

  /**
   * Get cancellation request statistics for organizer
   * @param {string} organizerId - The organizer's user ID
   * @returns {Promise<Object>} Statistics object
   */
  async getCancellationStats(organizerId) {
    const { data, error } = await supabase?.from('event_registrations')?.select('status, cancellation_requested_at, cancellation_processed_at', { count: 'exact' })?.eq('events.organizer_id', organizerId);

    if (error) throw error;

    const stats = {
      pendingRequests: 0,
      processedToday: 0,
      totalCancelled: 0
    };

    const today = new Date();
    today?.setHours(0, 0, 0, 0);

    data?.forEach(registration => {
      if (registration?.cancellation_requested_at && !registration?.cancellation_processed_at) {
        stats.pendingRequests++;
      }
      if (registration?.cancellation_processed_at) {
        const processedDate = new Date(registration.cancellation_processed_at);
        if (processedDate >= today) {
          stats.processedToday++;
        }
      }
      if (registration?.status === 'cancelled') {
        stats.totalCancelled++;
      }
    });

    return stats;
  }
};

/**
 * Update refund status for a cancellation request
 * @param {string} registrationId - Event registration ID
 * @param {string} refundStatus - New refund status (pending/processing/completed)
 * @param {Object} refundDetails - Additional refund details
 * @returns {Promise<Object>} - Updated registration
 */
export const updateRefundStatus = async (registrationId, refundStatus, refundDetails = {}) => {
  try {
    const updates = {
      refund_status: refundStatus,
      updated_at: new Date()?.toISOString()
    };

    // Add payment method if provided
    if (refundDetails?.paymentMethod) {
      updates.payment_method = refundDetails?.paymentMethod;
    }

    // Add expected arrival date if provided
    if (refundDetails?.expectedArrivalDate) {
      updates.expected_arrival_date = refundDetails?.expectedArrivalDate;
    }

    const { data, error } = await supabase?.from('event_registrations')?.update(updates)?.eq('id', registrationId)?.select(`
        *,
        event:events(
          title,
          event_date
        )
      `)?.single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error updating refund status:', error);
    throw error;
  }
};

/**
 * Get refund details for a cancelled registration
 * @param {string} registrationId - Event registration ID
 * @returns {Promise<Object>} - Refund details
 */
export const getRefundDetails = async (registrationId) => {
  try {
    const { data, error } = await supabase?.from('event_registrations')?.select('refund_status, payment_method, expected_arrival_date, refund_initiated_at, refund_completed_at')?.eq('id', registrationId)?.single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error fetching refund details:', error);
    throw error;
  }
};